'use client';
import {
  getLocalstorageValue,
  setLocalstorageValue,
  StorageKeys,
} from '@finn/ui-utils/ssr';
import {
  getSession as getSessionNext,
  GetSessionParams,
  useSession as useSessionNext,
} from 'next-auth/react';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { CookieKeys, setClientCookie } from '../lib/cookies';

// Client-readable hint that a better-auth session exists, mirrored from the
// httpOnly better-auth cookie by the UA middleware (kept accurate: cleared when
// the better-auth session is gone). Read straight from document.cookie — not via
// `getClientCookie` (typed to the CookieKeys enum) and not imported from
// `@finn/ua-auth` (would create a ui-utils → ua-auth cycle).
const BETTER_AUTH_HINT_COOKIE = 'finn_ba_session';
const hasBetterAuthSessionHint = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.cookie.split('; ').some((entry) => {
    const separatorIndex = entry.indexOf('=');

    return (
      entry.slice(0, separatorIndex) === BETTER_AUTH_HINT_COOKIE &&
      entry.slice(separatorIndex + 1).length > 0
    );
  });
};

export enum LoyaltyLevels {
  Silver = 'Silver',
  Gold = 'Gold',
  Platinum = 'Platinum',
  Black = 'Black',
}

export interface IFinnSession {
  user: {
    name: string;
    email: string;
    email_verified: string;
    image?: string;
    picture?: string;
    hs_contact_id?: string;
    hs_first_name?: string;
    hs_last_name?: string;
    hs_country?: string;
    hs_postalcode?: string;
    provider?: string;
    nextUrl?: string;
    is_job_auto?: boolean;
    job_auto_company?: {
      id?: number;
      name?: string;
      address?: string;
      domain?: string;
    };
    loyalty_level: LoyaltyLevels | null;
    user_role?: string | null;
    /**
     * JobAuto admin portal access: 'admin', or null for "not an admin".
     * Separate from `user_role`, which no longer carries the JobAuto admin
     * flag (OE-477).
     */
    jobauto_portal_role?: 'admin' | null;
    /**
     * What a B2B driver may do from the Customer Portal. Mirrors
     * `users_b2b.rights`: 'all' means they may also order a car, anything else
     * — including null, which means "not synced yet" — means they may not.
     * Read it through `canOrderCars()` rather than comparing here, so the
     * fail-closed default stays in one place.
     */
    user_rights?: 'operational' | 'all' | null;
  };
}

export enum SOCIAL_LOGIN_PROVIDER {
  GOOGLE = 'google',
  APPLE = 'apple',
}

// Imperative better-auth session fetcher, registered by BetterAuthProvider.
// The `useSession` HOOK below is better-auth-aware via `_betterAuthSessionGetter`,
// but the imperative `getSession()` promise is used right after login by the
// login/registration handlers — so it must prefer better-auth too. Without this,
// a user who just authenticated via better-auth (better-auth cookie, no next-auth
// cookie) reads as logged-out here, and the handlers skip all post-login logic
// (tracking, account-switch, checkout continuation).
type SessionFetcher = () => Promise<IFinnSession | null>;
let _betterAuthSessionFetcher: SessionFetcher | null = null;

export const setBetterAuthSessionFetcher = (
  fetcher: SessionFetcher | null
): void => {
  _betterAuthSessionFetcher = fetcher;
};

export const getSession = async (
  options?: GetSessionParams
): Promise<IFinnSession> => {
  if (_betterAuthSessionFetcher) {
    const baSession = await _betterAuthSessionFetcher();
    if (baSession) {
      return baSession;
    }
    // better-auth has no session — fall through to next-auth (overlap window,
    // preserves existing next-auth sessions).
  }

  const session = await getSessionNext(options);

  return session as unknown as IFinnSession;
};

// Session override for better-auth (APP-2231).
// When BetterAuthProvider is active, it registers a getter here.
// useSession checks this before falling back to next-auth.
type SessionGetter = () => [IFinnSession | null, boolean];
let _betterAuthSessionGetter: SessionGetter | null = null;

// Reactivity bridge: `_betterAuthSessionGetter` is a plain function, not React
// state, so updating it does NOT re-render the components that call useSession().
// The BetterAuthProvider bridge re-registers the getter every time the better-auth
// session (or its loading flag) changes; we bump a version and notify subscribers
// so every useSession() consumer re-renders and reads the fresh session. Without
// this, a component that first rendered while better-auth was still resolving
// stays stuck on "loading / no user" forever (e.g. the checkout wedged in its
// loading state after login — APP-2231), because nothing else re-renders it.
let _betterAuthVersion = 0;
const _betterAuthListeners = new Set<() => void>();

const subscribeBetterAuthSession = (onChange: () => void) => {
  _betterAuthListeners.add(onChange);

  return () => {
    _betterAuthListeners.delete(onChange);
  };
};

const getBetterAuthVersion = () => _betterAuthVersion;
// SSR / first client render: stable value so hydration never mismatches.
const getBetterAuthVersionServer = () => -1;

export const setBetterAuthSessionGetter = (getter: SessionGetter | null) => {
  _betterAuthSessionGetter = getter;
  _betterAuthVersion += 1;
  _betterAuthListeners.forEach((listener) => listener());
};

// The "still loading" session value. next-auth surfaces loading as `undefined`
// (not `null`) at runtime, and consumers distinguish loading from guest by that:
// `session === undefined` means loading, `=== null` means logged-out. useSession's
// return type stays `IFinnSession | null` (widening it to `| undefined` ripples a
// type change through every consumer), but — exactly as the next-auth branch below
// already does via its `as unknown as` cast — the runtime value is `undefined`.
const LOADING_SESSION = undefined as unknown as null;

export const useSession = (): [IFinnSession | null, boolean] => {
  // Re-render this consumer whenever the better-auth session getter changes
  // (bridge resolves/updates the session). Reads a version primitive so the
  // snapshot is stable; the actual session is read from the getter below.
  const betterAuthVersion = useSyncExternalStore(
    subscribeBetterAuthSession,
    getBetterAuthVersion,
    getBetterAuthVersionServer
  );

  // Always call useSessionNext unconditionally (React hooks must have
  // consistent call order between renders — no conditional hook calls)
  let nextAuthSession: IFinnSession | null = null;
  let nextAuthLoading = false;
  try {
    //eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: session, status } = useSessionNext() || {};
    nextAuthLoading = status === 'loading';
    // justified-any: next-auth v4 session type doesn't match IFinnSession shape
    nextAuthSession = session as unknown as IFinnSession;
  } catch (ex) {
    // fallback for b2b
  }

  // Hydrate the same next-auth snapshot used on the server before reading
  // browser-only state, even when the bridge resolved before this boundary.
  if (betterAuthVersion === -1) {
    return [nextAuthSession, nextAuthLoading];
  }

  // If better-auth is active, prefer its session
  if (_betterAuthSessionGetter) {
    const [baSession, baLoading] = _betterAuthSessionGetter();
    if (baSession) {
      return [baSession, false];
    }
    if (baLoading) {
      // better-auth is still resolving. If a legacy next-auth session already
      // exists, prefer it right now instead of reporting "loading / no user" —
      // otherwise a logged-in user is briefly treated as a guest and can be
      // shown a re-login prompt (Google One Tap / LoginNudge). Never flash
      // logged-out during the overlap window.
      if (nextAuthSession) {
        return [nextAuthSession, false];
      }

      // Loading (see LOADING_SESSION). Returning `null` here made a logged-in
      // better-auth user read as a resolved guest for the tick before the
      // session surfaced — a logged-out flash in the header and a spurious
      // re-login prompt (APP-2231). `undefined` keeps consumers in their
      // loading branch (header skeleton, One Tap, LoginNudge, checkout).
      return [LOADING_SESSION, true];
    }
    // better-auth resolved to no session — fall through to next-auth
    // (preserves existing sessions during migration)
  }

  // Overlap-window anti-flicker: a better-auth session exists (hint cookie) but
  // the read bridge hasn't surfaced it yet — the provider activates post-mount,
  // so on reload there's a tick where the getter isn't registered and next-auth
  // has already resolved to "no user". Report "loading", not "guest", so the
  // header (and One Tap / LoginNudge) render a skeleton instead of flashing
  // logged-out → logged-in. Only when next-auth itself has no session; if the
  // bridged next-auth cookie already resolved a user we return it above.
  //
  // Gated on the getter being ABSENT, which is what "hasn't surfaced it yet"
  // actually means. Without that guard this also fired once better-auth HAD
  // answered and fallen through above, so a user whose better-auth cookie
  // outlived its server session (revoked / expired / signed out elsewhere) got a
  // permanent skeleton instead of the logged-out state (APP-2231).
  if (
    !_betterAuthSessionGetter &&
    !nextAuthSession &&
    hasBetterAuthSessionHint()
  ) {
    // Loading, not guest (see LOADING_SESSION): `null` would skip the skeleton.
    return [LOADING_SESSION, true];
  }

  return [nextAuthSession, nextAuthLoading];
};

export const useSessionInStorage = (): IFinnSession | null | undefined => {
  const [savedSession, setSavedSession] = useState<
    IFinnSession | null | undefined
  >(undefined);
  const [sessionNext] = useSession();

  const savedSessionEmail = savedSession?.user?.email;

  // replace session in cookie and state with the new one from next-auth
  useEffect(() => {
    const sessionNextEmail = sessionNext?.user?.email;
    if (sessionNextEmail && sessionNextEmail !== savedSessionEmail) {
      setLocalstorageValue(
        StorageKeys.SESSION_DATA,
        {
          ...sessionNext,
          user: { ...sessionNext.user, nextUrl: undefined }, // nextUrl localhost causes issue with checkout api
        },
        24 * 7
      );
      setClientCookie(CookieKeys.SESSION_DATA, 'y'); // keep a cookie with small string for AB testing rewrite logic
      setSavedSession(sessionNext);
    }
  }, [savedSessionEmail, sessionNext]);

  // put the value in localstorage into the state
  useEffect(() => {
    const sessionInStorage = getLocalstorageValue(
      StorageKeys.SESSION_DATA
    ) as IFinnSession;
    const userEmail = sessionInStorage?.user?.email;
    if (userEmail && userEmail !== savedSession?.user?.email) {
      setSavedSession(sessionInStorage);
    } else if (savedSession === undefined) {
      setSavedSession(null);
    }
  }, [savedSession]);

  return sessionNext === undefined ? savedSession : sessionNext;
};

export const getFinnSessionId = (): string => {
  const requestId =
    new Date().getTime().toString(36) +
    Math.random().toString(36).substr(2, 12);

  return requestId;
};

export const isTestEmail = (email: string | undefined): boolean => {
  return Boolean(
    email?.toLocaleLowerCase?.().match?.(/.*\+test.*?@finn\.(auto|com)$/)
  );
};

export const getIsInternalUser = () => {
  if (typeof window === 'undefined') return false;

  const userInfo =
    JSON.parse(localStorage.getItem('ajs_user_traits') || '""') || null;
  const userEmail = userInfo?.email || null;

  return isTestEmail(userEmail);
};
